import {
	createImmutableState,
	getImmutableStateProperty,
	setImmutableStateProperty,
} from "@/utils/state";
import { defineStore } from "pinia";
import { computed, type ComputedRef } from "vue";

export interface FlashNews {
	id: string;
	title: string;
	message: string;
	start_date: Date;
	end_date: Date;
	created_at: Date;
}

export type NewsStoreState = {
	news: Array<FlashNews> | null;
};

interface NewsStore {
	news: ComputedRef<Array<FlashNews> | null>;
	latestNewsList: ComputedRef<Array<FlashNews>>;
	activePopupNews: ComputedRef<FlashNews | null>;
	$setState: (value: Array<FlashNews>) => void;
}

export default defineStore("news", (): NewsStore => {
	const state = createImmutableState<NewsStoreState>({
		news: null,
	});

	// Used only for save.
	// DO NOT USE IN GAME!
	const $setState = (value: Array<FlashNews>): void => {
		setImmutableStateProperty(state, "news", value);
	};

	const news: ComputedRef<Array<FlashNews> | null> =
		getImmutableStateProperty(state, "news", null);

	// Requirement 3: Display 10 latest news sorted by start_date (regardless of end_date)
	const latestNewsList = computed((): Array<FlashNews> => {
		if (!news.value) return [];

		return [...news.value]
			.sort(
				(a: FlashNews, b: FlashNews): number =>
					new Date(b.start_date).getTime() -
					new Date(a.start_date).getTime(),
			)
			.slice(0, 10);
	});

	// Requirement 4 & 4.a: Get active news for pop-up on game launch
	const activePopupNews = computed((): FlashNews | null => {
		if (!news.value) return null;

		const now = new Date().getTime();

		// Requirement 4: end_date determines the last date a news item can trigger as a pop-up
		const validPopupNews = news.value.filter((item: FlashNews): boolean => {
			const start = new Date(item.start_date).getTime();
			const end = new Date(item.end_date).getTime();
			return start <= now && now <= end;
		});

		if (validPopupNews.length === 0) return null;

		// Requirement 4.a: If multiple news meet pop-up conditions, return the latest one by start_date
		return validPopupNews.sort(
			(a: FlashNews, b: FlashNews): number =>
				new Date(b.start_date).getTime() -
				new Date(a.start_date).getTime(),
		)[0];
	});

	return {
		news,
		latestNewsList,
		activePopupNews,
		$setState,
	};
});
